import React, { useCallback, useEffect, useMemo, useState } from 'react';
import produce from 'immer';
import { orderBy, uniqBy } from 'lodash-es';
import { gql } from '@soundxyz/gql-string';
import { MAX_WAVEFORM_COMMENTS } from '@soundxyz/utils/const';
import { uuidv4 } from '@soundxyz/utils/random';
import { useLatestRef } from '@components-shared/src/hooks/useLatestRef';
import type { FragmentType } from '@graphql/reactQuery';
import {
  getFragment,
  makeFragmentData,
  ReleasePageDocument,
  setQueryData,
  WaveformCommentInfoFragmentDoc,
  WaveformCommentsReleaseInfoFragmentDoc,
} from '@graphql/reactQuery';
import { useSubscribeReleaseNftsComments } from '@hooks/useSubscribeRelease';
import { useAudioController } from '@store/audio/AudioController';
import { useAudioPosition } from '@store/audio/AudioPosition';
import { getSongSlotByAudioPosition } from '@utils/helpers';
import { AvatarWithComment } from './AvatarWithComment';

export type PlayCommentArgs = {
  commentId: string;
  commentTimestamp: number;
};

const MIN_COMMENT_OPEN_TIME_MS = 1000;
const MAX_COMMENT_OPEN_TIME_MS = 5000;
const CLOSE_COMMENT_DELAY_MS = 200;
const AVATAR_MOBILE_COLLAPSE_THRESHOLD = 20;

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment WaveformCommentInfo on NftWithComment {
    id
    songSlot
    avatarUrl
    ownerPublicAddress
    ownerWebappUri
    ownerArtistName
    ownerUsername
    updatedAt
    serialNumber
    ownerReleaseNftCount
    isGoldenEgg

    comment {
      id
      message
    }
  }

  fragment WaveformCommentsReleaseInfo on Release {
    id
    titleSlug
    artist {
      id
      soundHandle
    }
    track {
      id
      duration
    }
    topNftsWithComment {
      id
      serialNumber
      ...WaveformCommentInfo
    }
  }
`);

// TODO: https://linear.app/sound/issue/ENG-4932/consolidate-logic-for-waveform-comment-hero-and-expanded-player
export const WaveformComments = React.memo(function WaveformComments({
  width,
  seekToSec,
  release: releaseFragment,
}: {
  release: FragmentType<WaveformCommentsReleaseInfoFragmentDoc>;
  seekToSec: (sec: number) => void;
  width: number;
}) {
  const release = getFragment(WaveformCommentsReleaseInfoFragmentDoc, releaseFragment);

  useSubscribeReleaseNftsComments({
    releaseId: release.id,
    onData({ data: { releaseNftCommentUpdate } }) {
      // This doesn't really happen
      if (!release.artist.soundHandle) return;
      setQueryData(
        {
          query: ReleasePageDocument,
          variables: {
            soundHandle: release.artist.soundHandle,
            releaseSlug: release.titleSlug,
          },
        },
        previousData => {
          if (!previousData) return previousData;

          return produce(previousData, draft => {
            if (!draft.data.mintedRelease) return;

            const releaseDraftFragment = getFragment(
              WaveformCommentsReleaseInfoFragmentDoc,
              draft.data.mintedRelease,
            );

            if (releaseDraftFragment.topNftsWithComment.length >= MAX_WAVEFORM_COMMENTS) return;

            releaseDraftFragment.topNftsWithComment = orderBy(
              uniqBy(
                [
                  ...releaseNftCommentUpdate.map(nft => {
                    return {
                      id: nft.id,
                      serialNumber: nft.serialNumber,
                      ...makeFragmentData(
                        {
                          avatarUrl: nft.ownerAvatarUrl,
                          comment: {
                            id: uuidv4(),
                            message: nft.commentMessage,
                            updatedAt: new Date().toISOString(),
                          },
                          id: nft.id,
                          isGoldenEgg: nft.isGoldenEgg,
                          ownerArtistName: nft.ownerArtistName,
                          ownerPublicAddress: nft.ownerPublicAddress,
                          ownerReleaseNftCount: nft.ownerReleaseNftCount,
                          ownerUsername: nft.ownerUsername,
                          ownerWebappUri: nft.ownerWebappUri,
                          serialNumber: nft.serialNumber,
                          songSlot: nft.songSlot,
                          updatedAt: new Date(nft.updatedAt).getTime(),
                        },
                        WaveformCommentInfoFragmentDoc,
                      ),
                    };
                  }),
                  ...releaseDraftFragment.topNftsWithComment,
                ],
                v => v.id,
              ),
              v => v.serialNumber,
              'asc',
            ).slice(0, MAX_WAVEFORM_COMMENTS);
          });
        },
      );
    },
  });

  const commentsToDisplay = useMemo(
    () =>
      release.topNftsWithComment.map(comment =>
        getFragment(WaveformCommentInfoFragmentDoc, comment),
      ),
    [release.topNftsWithComment],
  );

  // Tracks the comment the user is currently hovering
  const [realTimeHoveredCommentId, setRealTimeHoveredCommentId] = useState<string | null>(null);
  const realTimeHoveredCommentIdRef = useLatestRef(realTimeHoveredCommentId);

  // Tracks if a comment is still open, even if the user is not hovering it anymore
  const [delayedHoveredCommentId, setDelayedHoveredCommentId] = useState<string | null>(null);

  // Tracks the comment currently shown in the dom, no matter if triggered by hover or playback
  const [openCommentId, setOpenCommentId] = useState<string | null>(null);
  const [openCommentTimestamp, setOpenCommentTimestamp] = useState<number>(0);

  const [closeEventId, setCloseEventId] = useState<string | null>(null);

  const { position } = useAudioPosition();
  const { activeTrackId, playing } = useAudioController();
  const isActiveTrack = activeTrackId === release.track.id;

  const playComment = useCallback(
    ({ commentId, commentTimestamp }: PlayCommentArgs): void => {
      setOpenCommentId(commentId); // This avoids the comment from closing when the user clicks on it, only to reopen when playback reaches next song slot
      seekToSec(commentTimestamp);
    },
    [seekToSec],
  );

  // Create a close event id so we can differentiate between new and repeated close events
  const delayedCloseComment = useCallback(() => {
    setCloseEventId(uuidv4());
  }, []);

  useEffect(() => {
    if (realTimeHoveredCommentId) {
      setDelayedHoveredCommentId(realTimeHoveredCommentId);
      realTimeHoveredCommentIdRef.current = realTimeHoveredCommentId;
    } else {
      delayedCloseComment();
    }
  }, [delayedCloseComment, realTimeHoveredCommentId, realTimeHoveredCommentIdRef]);

  // We want a short delay before comments close, to give the user time to move their cursor from tooltip trigger to tooltip content without it closing
  // We also want to avoid closing the comment if user hovers, then leaves, then hovers the same comment again
  // If we'd use comment id for this, the timeout won't be cleared in this useEffect, since the value remains the same, causing the comment to close unexpectedly
  // Instead, let's use an id that is generated when the pointer leaves either the comment or the avatar
  useEffect(() => {
    let timeout = 0;
    timeout = window.setTimeout(() => {
      if (!realTimeHoveredCommentIdRef.current) {
        setDelayedHoveredCommentId(null);
        setCloseEventId(null);
      }
    }, CLOSE_COMMENT_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [closeEventId, realTimeHoveredCommentIdRef]);

  useEffect(() => {
    // Prioritize hovered comments
    if (delayedHoveredCommentId) {
      setOpenCommentId(delayedHoveredCommentId);
      setOpenCommentTimestamp(0);
      return;
    }

    // Close comment if playback is stopped
    if (!playing) return setOpenCommentId(null);

    const now = Date.now();
    const timeOpenMs = now - openCommentTimestamp;

    // If current open comment has not been open for long enough, do nothing
    if (playing && isActiveTrack && timeOpenMs >= MIN_COMMENT_OPEN_TIME_MS) {
      const currentSongSlot = getSongSlotByAudioPosition({
        position,
        trackDuration: release.track.duration,
      });

      const commentToShow = commentsToDisplay?.find(
        comment => comment?.songSlot === currentSongSlot,
      );

      if (commentToShow) {
        setOpenCommentId(prevId => {
          // Store timestamp of when comment was first opened, so we can track if it's been open for long enough on next run
          if (prevId !== commentToShow.id) {
            setOpenCommentTimestamp(now);
          }
          return commentToShow.id;
        });
        // Close comment if it's been open for too long
      } else if (timeOpenMs >= MAX_COMMENT_OPEN_TIME_MS) {
        setOpenCommentId(null);
        setOpenCommentTimestamp(0);
      }
    }
  }, [
    position,
    activeTrackId,
    release.track.id,
    openCommentTimestamp,
    release.track.duration,
    commentsToDisplay,
    playing,
    isActiveTrack,
    delayedHoveredCommentId,
    realTimeHoveredCommentId,
  ]);

  const isOverCollapseThreshold = commentsToDisplay.length > AVATAR_MOBILE_COLLAPSE_THRESHOLD;

  return (
    <>
      {commentsToDisplay.map(nft => (
        <AvatarWithComment
          key={nft.id}
          nft={nft}
          isOpen={!!openCommentId && openCommentId === nft.id}
          setRealTimeHoveredCommentId={setRealTimeHoveredCommentId}
          playComment={playComment}
          release={release}
          width={width}
          isOverCollapseThreshold={isOverCollapseThreshold}
        />
      ))}
    </>
  );
});
